import {
  type ChildNode,
  type ConversationStyle,
  type CoreNodeConstraints,
  type CoreNodeEdge,
  EntityType,
  type Form,
  type StringMatchesConstraint
} from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  GetNodeAncestorsQuery,
  GetNodeAncestorsQueryVariables,
  NodeTitleFragment,
  NodeViewsQuery
} from '@aurora/shared-generated/types/graphql-types';
import { KeyBoardEvents } from '@aurora/shared-types/community/enums';
import { NodeType } from '@aurora/shared-types/nodes/enums';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import type { UseDropdownMenuOptions } from 'react-overlays/DropdownMenu';
import { SharedComponent } from '../../../enums';
import FormBuilder from '../../../helpers/form/FormBuilder/FormBuilder';
import { dropdownPopperConfig } from '../../../helpers/ui/PopperJsHelper';
import { focusFirstTabbableChildElement } from '../../../helpers/ui/KeyboardNavigationHelper/KeyboardNavigationHelper';
import Icons from '../../../icons';
import { FocusElementType, LoadingSize } from '../../../types/enums';
import Button from '../../common/Button/Button';
import { ButtonVariant } from '../../common/Button/enums';
import { IconSize } from '../../common/Icon/enums';
import Icon from '../../common/Icon/Icon';
import QueryHandler from '../../common/QueryHandler/QueryHandler';
import AppContext from '../../context/AppContext/AppContext';
import { FormCheckInputType } from '../../form/enums';
import InputEditForm from '../../form/InputEditForm/InputEditForm';
import type { FormSpec } from '../../form/types';
import nodeAncestorsQuery from '../../nodes/NodeAncestors.query.graphql';
import NodeTitle from '../../nodes/NodeTitle/NodeTitle';
import useQueryWithTracing from '../../useQueryWithTracing';
import useTranslation from '../../useTranslation';
import type { NodeSelectorDropdownItemDisabledProps } from '../NodeSelectorDropdownItem/NodeSelectorDropdownItem';
import NodeSelectorDropdownItem from '../NodeSelectorDropdownItem/NodeSelectorDropdownItem';
import type { GenericNode, NodePickerNode } from '../types';
import localStyles from './NodeSelectorDropdownMenu.module.pcss';
import NodeSelectorSearchSchema from './NodeSelectorSearchForm.form.json';
import useDebouncedQueryTerm from '../../../helpers/debounce/useDebouncedQueryTerm';
import useConstraintBuilder from '../../../helpers/constraints/useConstraintBuilder';
import AdvancedDropdownEmptyStateItem from '../../advancedDropdown/AdvancedDropdownEmptyStateItem';
import AdvancedDropdownPager from '../../advancedDropdown/AdvancedDropdownPager';
import { exactMatchOrStartsWith } from '../../../helpers/search/SearchTermHelper';
import useNodeSelectorDropdownNodeViews from './useNodeSelectorDropdownNodeViews';

interface Props {
  /**
   * Classname to apply to the component.
   */
  className?: string;
  /**
   * The popper.js config used by the menu
   */
  popperConfig?: UseDropdownMenuOptions['popperConfig'];
  /**
   * The currently selected node.
   */
  selectedNode: NodePickerNode;
  /**
   * Callback to set the selection.
   * @param node
   */
  onNodeSelect: (node: NodePickerNode) => void;
  /**
   * Whether the dropdown is open.
   */
  dropdownOpen: boolean;
  /**
   * Placeholder text for search box.
   */
  searchPlaceholderText?: string;
  /**
   * Whether to show empty nodes.
   */
  hideEmptyNodes?: boolean;
  /**
   * Whether to show search form on dropdown menu.
   */
  showSearchForm?: boolean;
  /**
   * The search term to filter node
   */
  nodeSearchTerm?: string;
  /**
   * The conversation styles of boards to be displayed in the menu along with other container nodes.
   * If specified, only boards of the specified conversation styles will be displayed along with container
   * nodes, else all nodes and boards are displayed
   */
  conversationStylesToBeIncluded?: Array<ConversationStyle>;
  /**
   * Props used to disable and style dropdown items in the NodeSelectorDropdownMenu
   */
  disabledProps?: NodeSelectorDropdownItemDisabledProps;
  /**
   * An array of node ids to exclude from the query results
   */
  excludeNodeIds?: Array<string>;
  /**
   * Whether nodes that are hidden should be displayed
   */
  showHiddenNodes?: boolean;
  /**
   * Array of NodeTypes to limit the picker to
   */
  nodeTypeConstraint?: Array<NodeType>;
}

interface SearchData {
  /**
   * The query term for node search.
   */
  searchTerm: string;
  /**
   * Whether local search is enabled.
   */
  enableLocalSearch: boolean;
}

/**
 * Node Selector Dropdown Menu
 * @constructor
 *
 * @author Soumen Porel
 */
const NodeSelectorDropdownMenu: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  popperConfig = dropdownPopperConfig,
  onNodeSelect,
  selectedNode,
  dropdownOpen,
  searchPlaceholderText,
  hideEmptyNodes = false,
  showSearchForm = true,
  nodeSearchTerm,
  conversationStylesToBeIncluded = [],
  disabledProps = {
    isDisabledFn: null,
    useDisabledOpacity: false,
    useDisabledOpacityForTitle: false,
    disabledReasonText: () => null,
    parentDisabledReasonText: null
  },
  excludeNodeIds = [],
  showHiddenNodes = false,
  nodeTypeConstraint = []
}) => {
  const cx = useClassNameMapper(localStyles);
  const constraintBuilder = useConstraintBuilder<CoreNodeConstraints>();
  const i18n = useTranslation(SharedComponent.NODE_SELECTOR_DROPDOWN_MENU);
  const { loading: textLoading, formatMessage } = i18n;
  const { community } = useContext(AppContext);
  const { isDisabledFn, useDisabledOpacity, useDisabledOpacityForTitle, parentDisabledReasonText } =
    disabledProps;
  const isCommunityParentNodeDisabled: boolean =
    !!isDisabledFn && isDisabledFn(community as unknown as GenericNode);

  const communityWithDepth = useMemo(
    () => ({ ...community, depth: 0, entityType: EntityType.Community }),
    [community]
  );
  /**
   * default parent :  gets the parent based on the selected node.
   * for selected Container nodes -> parent is also the selected container node
   * for selected boards -> if depth is 1 then parent is community else parent is selectedNode's parent
   * for selected community -> parent remains the community
   */
  const defaultParent = (
    selectedNode.nodeType === NodeType.CATEGORY || selectedNode.nodeType === NodeType.GROUPHUB
      ? selectedNode
      : selectedNode.depth != 1
      ? selectedNode.parent
      : community
  ) as NodePickerNode;
  const [parentNode, setParentNode] = useState<NodePickerNode>(
    selectedNode.id === community.id ? communityWithDepth : defaultParent
  );
  const parentStack = useRef<NodePickerNode[]>([]);

  const [queryTerm, setQueryTerm] = useState('');
  const queryTermThreshold = 3;
  const { value: debouncedQueryTerm, setValue: setDebouncedQueryTerm } = useDebouncedQueryTerm({
    queryTerm,
    queryTermThreshold
  });

  const [searchLocal, setSearchLocal] = useState(false);
  const searchEnabled = useRef<boolean>(false);
  const parentNodeRef = useRef(null);
  const isSelectedNodeCommunity = selectedNode.id === community.id || selectedNode?.depth === 1;

  const queryTermClause: StringMatchesConstraint = {
    matches: exactMatchOrStartsWith(debouncedQueryTerm)
  };

  const formRef = useRef<HTMLFormElement>(null);

  const shouldFilterByConversationStyle = conversationStylesToBeIncluded.length > 0;

  function setParentWithStackAndRefUpdate(node: NodePickerNode) {
    setParentNode(node);
    parentStack.current?.push(node.parent);
    parentNodeRef.current?.focus();
  }

  /**
   * Handles the navigation to the parent category.
   * @param currentParent the parent Node to navigate to
   * @param event the event
   */
  function handleNavigationToParent(currentParent: NodePickerNode, event): void {
    if (currentParent.nodeType === NodeType.COMMUNITY) {
      if (!shouldFilterByConversationStyle) {
        onNodeSelect(communityWithDepth);
      }
    } else if (currentParent.depth === 1) {
      setParentNode(communityWithDepth);
      event.stopPropagation();
    } else {
      setParentNode(parentStack.current.pop());
      event.stopPropagation();
    }
  }

  /** Abstracts the implementation of getting the selector button from the rest of the code. This will make it easier to convert to using refs later. TODO */
  function getSelectorButton(idx: number): HTMLButtonElement | null {
    const selector = `[data-node-selector-btn="${idx}"]`;
    return document.querySelector<HTMLButtonElement>(selector);
  }

  /** Abstracts the implementation of getting the expand button from the rest of the code. This will make it easier to convert to using refs later. TODO */
  function getExpandButton(idx: number): HTMLButtonElement | null {
    const selector = `[data-node-expand-btn="${idx}"]`;
    return document.querySelector<HTMLButtonElement>(selector);
  }

  /**
   * Handles the navigation to the parent category via keyboard.
   * @param currentParent the parent Node to navigate to
   * @param event the event
   */
  function handleKeyNavigationToParent(currentParent: NodePickerNode, event): void {
    if (event.key === KeyBoardEvents.ENTER || event.key === KeyBoardEvents.ARROW_LEFT) {
      handleNavigationToParent(currentParent, event);

      if (currentParent.nodeType !== NodeType.COMMUNITY) {
        event.preventDefault();
      }
    } else if (event.shiftKey && event.key === KeyBoardEvents.TAB) {
      event.stopPropagation();
      if (currentParent.id === communityWithDepth.id) {
        setTimeout(() => focusFirstTabbableChildElement(formRef.current), 0);
      }
    } else if (event.key === KeyBoardEvents.ARROW_DOWN || event.key === KeyBoardEvents.TAB) {
      event.stopPropagation();
      event.preventDefault();
      const element = !shouldFilterByConversationStyle ? getSelectorButton(0) : getExpandButton(0);
      element?.focus();
    }
  }

  /**
   * Handles the expansion of the node to view its contents.
   * @param node the node to be expanded
   * @param event the event
   */
  function handleExpandNode(node: NodePickerNode, event): void {
    setParentWithStackAndRefUpdate(node);
    event.stopPropagation();
  }

  /**
   * Handles click on the Search Close icon.
   */
  const handleCloseSearch = useCallback(() => {
    searchEnabled.current = false;
    setQueryTerm('');
    setDebouncedQueryTerm('');
  }, [setDebouncedQueryTerm]);

  /**
   * Get the constraints that should be applied to both node browsing and node searching
   * @returns CoreNodeConstraints
   */
  function getCoreNodeConstraints(): CoreNodeConstraints {
    return constraintBuilder.build(
      {},
      {
        constraints: {
          includeHidden: { eq: true },
          nodeType: { in: nodeTypeConstraint }
        },
        conditions: {
          includeHidden: showHiddenNodes,
          nodeType: nodeTypeConstraint?.length > 0
        }
      }
    );
  }

  const coreNodeConstraints = getCoreNodeConstraints();

  /**
   * Get the contraints that should be applied only to node browsing
   * @returns CoreNodeConstraints
   */
  function getCoreNodeBrowseConstraints(): CoreNodeConstraints {
    const isParentCommunity = parentNode.id === community.id;

    return constraintBuilder.build(coreNodeConstraints, {
      constraints: {
        topicsCount: { gt: 0 },
        depth: { eq: 1 },
        parentId: { eq: parentNode.id }
      },
      conditions: {
        topicsCount: hideEmptyNodes,
        depth: isParentCommunity,
        parentId: !isParentCommunity
      }
    });
  }
  /**
   * Get the constraints that should only be applied to node search
   * @returns CoreNodeCOntraints
   */
  function getCoreNodeSearchConstraints(): CoreNodeConstraints {
    return constraintBuilder.build(coreNodeConstraints, {
      constraints: {
        title: queryTermClause,
        conversationStyle: { in: conversationStylesToBeIncluded },
        ancestorId: searchLocal ? { eq: parentNode.id } : { eq: community.id },
        excludeNodes: { in: excludeNodeIds }
      },
      conditions: {
        title: true,
        conversationStyle: shouldFilterByConversationStyle,
        ancestorId: true,
        excludeNodes: excludeNodeIds.length > 0
      }
    });
  }

  useEffect(() => {
    if (!dropdownOpen) {
      setParentNode(selectedNode.id === communityWithDepth.id ? communityWithDepth : defaultParent);
    }
    if (!dropdownOpen && searchEnabled.current) {
      handleCloseSearch();
    } else if (dropdownOpen && showSearchForm) {
      focusFirstTabbableChildElement(formRef.current);
    }
    setSearchLocal(false);
    if (nodeSearchTerm && nodeSearchTerm?.length >= queryTermThreshold) {
      searchEnabled.current = true;
      setQueryTerm(nodeSearchTerm);
    }
  }, [
    communityWithDepth,
    defaultParent,
    dropdownOpen,
    handleCloseSearch,
    selectedNode.id,
    nodeSearchTerm,
    showSearchForm
  ]);

  const constraints = searchEnabled.current
    ? getCoreNodeSearchConstraints()
    : getCoreNodeBrowseConstraints();
  const skip = searchEnabled.current && debouncedQueryTerm?.length === 0;
  const { queryResult, paginationHelper } = useNodeSelectorDropdownNodeViews(constraints, skip);
  const { data: nodeData } = queryResult;

  /**
   * When an inner node of depth > 2 is selected by default,
   * query all ancestors of the parent of the selected node to be added in the parentStack
   */
  useQueryWithTracing<GetNodeAncestorsQuery, GetNodeAncestorsQueryVariables>(
    module,
    nodeAncestorsQuery,
    {
      variables: {
        id: parentNode.id
      },
      skip: isSelectedNodeCommunity,
      onCompleted: data => {
        /**
         * When an inner node of depth > 2 is selected by default,
         * push all ancestors of the parent of the selected node into the parentStack in ascending order
         */
        parentStack.current = (data.coreNode as ChildNode).ancestors.edges.map(
          (edgeNode: CoreNodeEdge) => {
            return edgeNode.node;
          }
        );
      }
    }
  );

  if (textLoading) {
    return null;
  }

  /**
   * Handles selection in the dropdown.
   * @param node the selected node
   */
  function handleSelection(node: NodePickerNode): void {
    onNodeSelect(node);
    if (searchEnabled.current) {
      handleCloseSearch();
      setParentNode(
        node.id === communityWithDepth.id || node.depth === 1 ? communityWithDepth : node.parent
      );
    }
  }

  /**
   * Handles the expansion of the node to view its contents via keyboard.
   * @param node the node to be expanded
   * @param event the event
   */
  function handleKeyBoardNavigation(node: NodePickerNode, event): void {
    const currentElement: HTMLElement = event.target as HTMLElement;
    const searchField = document.querySelector('#NodeSelectorSearchForm-searchTerm') as HTMLElement;
    const checkField = document.querySelector(
      '#NodeSelectorSearchForm-enableLocalSearch'
    ) as HTMLElement;

    if (!currentElement.dataset.value) {
      if (event.key === KeyBoardEvents.ARROW_DOWN && searchEnabled.current) {
        event.preventDefault();
        const firstListItem = getSelectorButton(0);

        if (currentElement === searchField) {
          (checkField || firstListItem).focus();
        } else {
          firstListItem?.focus();
        }
      } else if (event.key === KeyBoardEvents.ARROW_UP && searchEnabled.current) {
        searchField.focus();
      }
    } else {
      const attributeValue = currentElement.dataset.value.split('-');
      const [dataType, idxString] = attributeValue;
      const idx = Number.parseInt(idxString, 10);

      let element: HTMLElement;
      switch (event.key) {
        case KeyBoardEvents.ARROW_RIGHT: {
          element = getExpandButton(idx);
          element?.focus();
          break;
        }
        case KeyBoardEvents.ARROW_LEFT: {
          element = getSelectorButton(idx);
          element?.focus();
          break;
        }
        case KeyBoardEvents.ARROW_DOWN: {
          event.stopPropagation();
          event.preventDefault();

          if (dataType === FocusElementType.NODE_DETAIL && !shouldFilterByConversationStyle) {
            element = getSelectorButton(idx + 1);
          } else {
            element = getExpandButton(idx + 1);
          }
          if (!element) {
            element = getSelectorButton(idx + 1);
          }
          if (!element) {
            element = document.querySelector('#show-more-button') as HTMLElement;
          }
          element?.focus();
          break;
        }
        case KeyBoardEvents.ARROW_UP: {
          event.stopPropagation();
          if (idx - 1 < 0) {
            if (parentNodeRef.current) {
              parentNodeRef.current.focus();
            } else {
              (checkField || searchField).focus();
            }
          } else {
            if (dataType === FocusElementType.NODE_DETAIL) {
              element = getSelectorButton(idx - 1);
            } else {
              element = getExpandButton(idx - 1);
            }
            element?.focus();
          }
          break;
        }
        case KeyBoardEvents.ENTER: {
          if (dataType === FocusElementType.NODE_EXPAND) {
            setParentWithStackAndRefUpdate(node);
          } else {
            onNodeSelect(node);
          }
          event.preventDefault();
          event.stopPropagation();
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  /**
   * Renders the parent node component in the dropdown menu
   *
   * @param parent parent node for the current state of dropdown menu
   */
  function renderParentNode(parent: NodePickerNode): React.ReactElement {
    const isCommunityParent = parent.id === community.id;
    const isDropdownItemDisabled = isCommunityParent && isCommunityParentNodeDisabled;
    const isButtonDisabled =
      (shouldFilterByConversationStyle && isCommunityParent) || isDropdownItemDisabled;
    const useDisabledStyles = isButtonDisabled && useDisabledOpacity;
    const useDisabledStylesForTitle = isButtonDisabled && useDisabledOpacityForTitle;

    return (
      <Dropdown.Item
        disabled={isDropdownItemDisabled}
        as="section"
        className={cx('lia-parent-wrap')}
        ref={parentNodeRef}
        onKeyDown={event => handleKeyNavigationToParent(parentNode, event)}
      >
        <Button
          disabled={isButtonDisabled}
          title={isButtonDisabled ? parentDisabledReasonText : undefined}
          variant={ButtonVariant.UNSTYLED}
          className={cx('lia-parent-btn', {
            'lia-parent-btn-style': !shouldFilterByConversationStyle,
            'lia-hidden-disabled': !useDisabledStyles,
            'lia-hidden-disabled-title': !useDisabledStylesForTitle
          })}
          testId="NodeSelectorDropdownMenu.ParentNodeBtn"
          onClick={event => handleNavigationToParent(parentNode, event)}
        >
          {isCommunityParent ? (
            <Icon
              size={IconSize.PX_16}
              icon={Icons.HomeIcon}
              className={cx('lia-g-m-5 flex-shrink-0 lia-home-icon')}
            />
          ) : (
            <Icon
              size={IconSize.PX_16}
              icon={Icons.ChevronLeftIcon}
              className={cx('lia-g-m-5 flex-shrink-0')}
            />
          )}
          <NodeTitle
            node={parentNode as NodeTitleFragment}
            as="span"
            className={cx('lia-parent-title')}
          />
        </Button>
      </Dropdown.Item>
    );
  }

  /** Filters and processes the raw data into usable menu items */
  function getNodeMenuItems(data: NodeViewsQuery): NodePickerNode[] {
    const nodes = data?.coreNodes?.edges.filter(edge => {
      if (shouldFilterByConversationStyle) {
        return (
          edge?.node.nodeType === NodeType.GROUPHUB ||
          edge?.node.nodeType === NodeType.CATEGORY ||
          ('conversationStyle' in edge?.node &&
            conversationStylesToBeIncluded.includes(edge?.node.conversationStyle))
        );
      } else {
        return true;
      }
    });

    const nodeItemMenu = nodes.map(edge => {
      return edge?.node;
    });

    return nodeItemMenu;
  }

  /**
   * Renders the dropdown menu items.
   * @param data the data obtained from the query results
   */
  function renderMenuItems(data: NodeViewsQuery): React.ReactElement {
    const nodeItemMenu = getNodeMenuItems(data);
    const isEmpty = nodeItemMenu.length === 0;
    return (
      <>
        {!isEmpty ? (
          nodeItemMenu?.map((node: NodePickerNode, idx: number) => (
            <NodeSelectorDropdownItem
              key={`${node.id}-${idx}`}
              node={node}
              isSelected={selectedNode.id === node.id}
              searchEnabled={searchEnabled.current}
              disabledProps={disabledProps}
              onSelect={handleSelection}
              onKeyDown={handleKeyBoardNavigation}
              onExpandNode={handleExpandNode}
              idx={idx}
            />
          ))
        ) : (
          <AdvancedDropdownEmptyStateItem
            description={
              !searchEnabled.current
                ? formatMessage('NodesFetching.noResults.message')
                : formatMessage('NodesSearch.noResults.message')
            }
            title={formatMessage('NodesFetching.noResults.title')}
          />
        )}

        <AdvancedDropdownPager
          className={cx('lia-node-picker-pager')}
          pageInfo={paginationHelper.pageInfo}
          loadPage={paginationHelper.loadPage}
        />
      </>
    );
  }

  /**
   * Handles onSubmit for the Search form.
   * @param formData
   */
  function handleSubmit(formData: SearchData): Promise<void> {
    const propertyExists = Object.keys(formData).find(element => element === 'enableLocalSearch');
    if (propertyExists) {
      setSearchLocal(formData.enableLocalSearch);
    }
    if (formData.searchTerm?.length >= queryTermThreshold) {
      searchEnabled.current = true;
      setQueryTerm(formData.searchTerm);
    } else if (formData.searchTerm?.length === 0) {
      searchEnabled.current = false;
      setQueryTerm('');
    }
    return null;
  }

  const formSpecs: FormSpec<SearchData> = new FormBuilder<SearchData>(
    'NodeSelectorSearchForm',
    i18n,
    { schema: NodeSelectorSearchSchema as Form, cx }
  )
    .addFieldGroupProperties('searchGroup', { className: cx('lia-g-mb-15') })
    .addSearchField({
      name: 'searchTerm',
      className: cx('lia-search'),
      useSearchIcon: false,
      formGroupSpec: {
        label: false
      },
      focus: true,
      defaultValue: '',
      onClearSearch: handleCloseSearch,
      placeholderText: searchPlaceholderText,
      ariaLabel: searchPlaceholderText,
      onKeyDown: event => handleKeyBoardNavigation(undefined, event)
    })
    .addCheckField({
      defaultValue: false,
      name: 'enableLocalSearch',
      inputType: FormCheckInputType.CHECKBOX,
      className: cx('lia-g-ml-20'),
      onKeyDown: event => handleKeyBoardNavigation(undefined, event),
      isVisible: {
        watchFields: null,
        callback: () => {
          return parentNode?.id !== community.id;
        }
      }
    })
    .build();

  return (
    <Dropdown.Menu
      popperConfig={popperConfig}
      data-testid="NodeSelectorMenu"
      className={cx('lia-menu', className)}
      aria-expanded={dropdownOpen}
      renderOnMount
      aria-live="polite"
    >
      {showSearchForm && (
        <InputEditForm
          formRef={formRef}
          formSpec={formSpecs}
          onSubmit={(formData: SearchData) => handleSubmit(formData)}
          submitOnChange={{
            watchFields: null
          }}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...{
            resetForm: queryTerm ? null : { searchTerm: '', enableLocalSearch: searchLocal }
          }}
        />
      )}
      {!searchEnabled.current && (
        <>
          {renderParentNode(parentNode)}
          <Dropdown.Divider className={cx('lia-g-mt-10 lia-g-mb-5')} />
        </>
      )}
      <>
        <QueryHandler queryResult={queryResult} loadingSize={LoadingSize.SM}>
          {(): React.ReactNode => {
            return renderMenuItems(nodeData);
          }}
        </QueryHandler>
      </>
    </Dropdown.Menu>
  );
};

export default NodeSelectorDropdownMenu;
